"use client";

import { CSSProperties, ReactNode, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { IQeChartTrader } from "@/sanity/pages/PageQuarterlyEventTemplate";

/* Live chart of the 4 funded accounts, drawn JAN→OCT in 16 s, then kept LIVE (the window scrolls one point every
   260 ms and each account drifts a little). Derived from the prototype's `#battle` engine
   (public/previews/iq-top1-files-lp, script "gráfico batalha ao vivo") but it is NOT a race any more:
   - every account keeps its own band for the whole animation — the curves never cross (rank-locked data),
   - the featured (mint) curve is fixed: Order Flow Expert, like the Figma frame (area, ring, dashed cursor, mint box),
   - every trader box has a fixed slot: desktop boxes ride their own tip, in rank order, stacked without overlap;
     mobile keeps Figma's legend under the plot and only tints the featured trader.
   Geometry is Figma's plot (desktop 1102 × 507, mobile 345 × 220). Reduced motion = the final frame of the draw. */

type Geo = { w: number; h: number; x0: number; x1: number; y0: number; y1: number };
const GEO: Record<"d" | "m", Geo> = {
  d: { w: 1102, h: 507.12, x0: 14.39, x1: 1054.38, y0: 70.46, y1: 477.32 },
  m: { w: 345, h: 220, x0: 12.11, x1: 293, y0: 16, y1: 184 },
};
const MAX = 40, N = 180, DUR = 16000, STEP = 260;
// Accounts in CMS order (traders[i]): Trading World Champion, Ex-Hedge Fund Manager, #1 Pro Scalper, Order Flow Expert.
// Ranks are fixed top → bottom in that order; END is where each curve settles (Figma: 37 / 30 / 23 / ~17 %).
const END = [37, 30, 23, 16];
const GAP = 2.4; // minimum distance (in %) between two neighbouring curves once they have spread out
const FEATURED = 3; // Figma's mint curve
const rnd = (s: number) => () => ((s = (s * 16807) % 2147483647), s / 2147483647);
// the exponents spread the curves early; the wobble fades out at both ends so every curve lands exactly on END
const wob = (t: number) => 4 * t * (1 - t);
const SHAPE = [
  (t: number) => END[0] * Math.pow(t, 0.85) + 2.2 * Math.sin(t * 6.1 + 0.4) * wob(t),
  (t: number) => END[1] * Math.pow(t, 0.95) + 2.4 * Math.sin(t * 7.3 + 1.1) * wob(t),
  (t: number) => END[2] * Math.pow(t, 1.05) + 1.8 * Math.sin(t * 5.4 + 2.0) * wob(t),
  (t: number) => END[3] * Math.pow(t, 1.15) + 0.8 * Math.sin(t * 4.2 + 0.6) * wob(t),
];
/** Keeps the ranks: each curve stays at least `gap` below the one above it — but never more than halfway to 0, so no
    curve gets pinned flat on the baseline while the bands are still opening. */
const rankLock = (v: number[], gap: number) => {
  v[0] = Math.max(0, v[0]);
  for (let k = 1; k < v.length; k++) v[k] = Math.max(0, Math.min(v[k], v[k - 1] - Math.min(gap, v[k - 1] / 2)));
  return v;
};

export function TraderBox({ t, className, style }: { t: IQeChartTrader; className?: string; style?: CSSProperties }) {
  return (
    <div className={cn("qe-bt-tag flex items-center gap-[1.5rem] overflow-hidden rounded-[0.5rem] border-solid p-[1rem]", className)} style={{ borderWidth: "0.124rem", ...style }}>
      <span className="qe-bt-av relative h-[5.706rem] w-[5.706rem] shrink-0 overflow-hidden rounded-[0.25rem] border-solid" style={{ borderWidth: "0.1861rem" }}>
        {t.avatar?.src && (
          /* eslint-disable-next-line @next/next/no-img-element */
          <img src={t.avatar.src} alt="" className="absolute inset-0 h-full w-full max-w-none object-cover [image-rendering:pixelated]" />
        )}
      </span>
      <span className="flex flex-col items-start gap-[0.496rem]">
        <span className="flex items-center font-geist text-[1.75rem] leading-[2.481rem] font-medium whitespace-nowrap text-white">
          {t.name}
        </span>
        {t.badge && <span className="qe-bt-bd rounded-[0.248rem] px-[0.744rem] py-[0.124rem] font-geist text-[1.75rem] leading-[2.481rem] font-medium whitespace-nowrap text-[#0b0b0b]">{t.badge}</span>}
      </span>
    </div>
  );
}

export default function QeBattle({ variant, traders, children }: { variant: "d" | "m"; traders: IQeChartTrader[]; children?: ReactNode }) {
  const plotRef = useRef<HTMLDivElement>(null);
  const gRef = useRef<SVGGElement>(null);
  const tagRefs = useRef<(HTMLDivElement | null)[]>([]);
  const g = GEO[variant];
  const gid = `qe-btg-${variant}`;

  useEffect(() => {
    const plot = plotRef.current, svg = gRef.current;
    if (!plot || !svg) return;
    const NS = "http://www.w3.org/2000/svg";
    const red = matchMedia("(prefers-reduced-motion: reduce)").matches;
    const { w: W, h: H, x0: X0, x1: X1, y0: Y0, y1: Y1 } = g;
    // seeded data, rank-locked point by point: the gap opens over the first 30 % of the span, then stays
    const noise = SHAPE.map((_, k) => {
      const r = rnd(17 + k * 31);
      let n = 0;
      return (t: number) => (n = n * 0.8 + (r() - 0.5) * (k === FEATURED ? 0.5 : 1.3) * Math.min(1, 0.3 + t * 2));
    });
    const D: number[][] = SHAPE.map(() => []);
    for (let j = 0; j <= N; j++) {
      const t = j / N;
      const v = rankLock(SHAPE.map((sh, k) => sh(t) + (j ? noise[k](t) : 0)), GAP * Math.min(1, t / 0.3));
      v.forEach((x, k) => D[k].push(x));
    }
    const X = (j: number) => X0 + ((X1 - X0) * j) / N, Y = (v: number) => Y1 - ((Y1 - Y0) * v) / MAX;
    const el = (t: string, a: Record<string, string | number>) => {
      const e = document.createElementNS(NS, t);
      for (const k in a) e.setAttribute(k, String(a[k]));
      svg.appendChild(e);
      return e;
    };
    svg.replaceChildren();
    const cur = el("line", { y1: Y0, y2: Y1, stroke: "#9ee3b0", "stroke-opacity": 0.35, "stroke-dasharray": "3 4" });
    const area = el("path", { fill: `url(#${gid})` });
    const lines = SHAPE.map((_, i) => el("path", { fill: "none", "stroke-linejoin": "round", "stroke-linecap": "round", stroke: i === FEATURED ? "#9ee3b0" : "#fff", "stroke-opacity": i === FEATURED ? 1 : 0.42, "stroke-width": i === FEATURED ? 2.6 : 1.5 }));
    const dots = SHAPE.map((_, i) => el("circle", { r: 5, stroke: "#0b0b0b", "stroke-width": 2, fill: i === FEATURED ? "#9ee3b0" : "#fff" }));
    const ring = el("circle", { r: 9, fill: "none", stroke: "#9ee3b0", "stroke-width": 1.2, class: "qe-bt-ring" });
    svg.appendChild(lines[FEATURED]);
    svg.appendChild(dots[FEATURED]);
    const tg = SHAPE.map((_, i) => tagRefs.current[i]);
    tg.forEach((e, i) => e?.classList.toggle("qe-lead", i === FEATURED));
    const SM: number[] = [];

    const paint = (val: number[], xe: number, path: (i: number) => string, below: boolean) => {
      D.forEach((_, i) => {
        const s = path(i);
        lines[i].setAttribute("d", s);
        dots[i].setAttribute("cx", String(xe));
        dots[i].setAttribute("cy", String(Y(val[i])));
        if (i === FEATURED) area.setAttribute("d", `${s}L${xe.toFixed(1)} ${Y1}L${X0} ${Y1}Z`);
      });
      ring.setAttribute("cx", String(xe));
      ring.setAttribute("cy", String(Y(val[FEATURED])));
      cur.setAttribute("x1", String(xe));
      cur.setAttribute("x2", String(xe));
      if (variant !== "d" || !tg.every(Boolean)) return;
      // boxes ride their own tip: eased, in fixed rank order (top → bottom), stacked without overlap, kept inside the plot
      const bw = plot.clientWidth, bh = plot.clientHeight, sx = bw / W, sy = bh / H;
      const hh = tg[0]!.offsetHeight || 62 * sx, gap = hh + 8 * sx, m = 6 * sx;
      val.forEach((v, i) => {
        const tgt = Math.min(Math.max(m, Y(v) * sy - hh / 2), bh - hh - m);
        SM[i] = SM[i] == null ? tgt : SM[i] + (tgt - SM[i]) * 0.18;
      });
      let lst = -1e9;
      const fy = SM.map((y) => (lst = Math.max(y, lst + gap)));
      const over = lst + hh - (bh - m);
      if (over > 0) for (let i = 0; i < fy.length; i++) fy[i] -= over;
      tg.forEach((e, i) => {
        const bwid = e!.offsetWidth, tip = xe * sx;
        const tx = below || tip + 16 * sx + bwid > bw - 64 * sx ? Math.max(m, tip - 16 * sx - bwid) : tip + 16 * sx;
        e!.style.transform = `translate(${tx.toFixed(0)}px,${fy[i].toFixed(0)}px)`;
        e!.style.opacity = "1";
      });
    };

    const draw = (p: number) => {
      const fp = Math.min(1, p) * N, k = Math.floor(fp), f = fp - k;
      const val = D.map((d) => (k >= N ? d[N] : d[k] + (d[k + 1] - d[k]) * f));
      const xe = X0 + (X1 - X0) * Math.min(1, p);
      paint(val, xe, (i) => {
        const d = D[i];
        let s = `M${X(0)} ${Y(d[0]).toFixed(1)}`;
        for (let j = 1; j <= k; j++) s += `L${X(j).toFixed(1)} ${Y(d[j]).toFixed(1)}`;
        return `${s}L${xe.toFixed(1)} ${Y(val[i]).toFixed(1)}`;
      }, false);
    };

    // after the draw: LIVE — the window scrolls and each account drifts gently around its own level; ranks never change
    const lr = rnd(991), drift = [0, 0, 0, 0];
    const pushTick = () => {
      const nv = D.map((d, i) => {
        const v = d[N];
        drift[i] = drift[i] * 0.85 + (lr() - 0.5) * (i === FEATURED ? 0.12 : 0.3);
        return Math.min(MAX - 1, v + drift[i] + (END[i] - v) * 0.08 + (lr() - 0.5) * (i === FEATURED ? 0.2 : 0.5));
      });
      rankLock(nv, GAP).forEach((v, i) => { D[i].push(v); D[i].shift(); });
    };
    const drawLive = (f: number) => {
      const val = D.map((d) => d[N - 1] + (d[N] - d[N - 1]) * f);
      paint(val, X1, (i) => {
        const d = D[i];
        let s = "";
        for (let j = 0; j <= N; j++) s += `${j ? "L" : "M"}${(X(j) - ((X1 - X0) / N) * f).toFixed(1)} ${Y(j < N ? d[j] : val[i]).toFixed(1)}`;
        return s;
      }, true);
    };

    if (red) { draw(1); const onR = () => draw(1); addEventListener("resize", onR); return () => removeEventListener("resize", onR); }

    let vis = false, t0: number | null = null, live = false, lastStep = 0, raf = 0;
    const io = new IntersectionObserver((e) => { vis = e[0].isIntersecting; }, { threshold: 0.2 });
    io.observe(plot);
    const loop = (now: number) => {
      raf = requestAnimationFrame(loop);
      if (!vis) return;
      if (t0 === null) t0 = now;
      const e = now - t0;
      if (!live) {
        if (e < DUR) return draw(e / DUR);
        live = true;
        lastStep = now;
        return draw(1);
      }
      if (now - lastStep > STEP * 20) lastStep = now - STEP; // back from off-screen: resume, don't replay the gap
      while (now - lastStep >= STEP) { pushTick(); lastStep += STEP; }
      drawLive((now - lastStep) / STEP);
    };
    draw(0.03);
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); io.disconnect(); };
  }, [g, gid, variant]);

  const svg = (
    <svg aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full overflow-visible" viewBox={`0 0 ${g.w} ${g.h}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#9ee3b0" stopOpacity="0.1" />
          <stop offset="1" stopColor="#9ee3b0" stopOpacity="0" />
        </linearGradient>
      </defs>
      <g ref={gRef} />
    </svg>
  );

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: BATTLE_CSS }} />
      {variant === "d" ? (
        <div ref={plotRef} className="relative h-[63.39rem] w-[137.75rem]">
          {children}
          {svg}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0">
            {traders.map((t, i) => (
              <div key={t._key ?? i} ref={(e) => { tagRefs.current[i] = e; }} className="absolute top-0 left-0 opacity-0 will-change-transform">
                <TraderBox t={t} />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
          <div ref={plotRef} className="relative h-[27.5rem] w-[43.125rem] lg:hidden">
            {children}
            {svg}
          </div>
          <div className="flex w-full flex-col gap-[0.75rem] lg:hidden">
            {traders.map((t, i) => (
              <div key={t._key ?? i} ref={(e) => { tagRefs.current[i] = e; }}>
                <TraderBox t={t} className="w-full" />
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

const BATTLE_CSS = `
.qe-bt-tag{background-color:#16191a;border-color:rgba(255,255,255,.12);transition:background-color .5s,border-color .5s}
.qe-bt-av{border-color:rgba(255,255,255,.2);transition:border-color .5s}
.qe-bt-bd{background-color:rgba(255,255,255,.9);transition:background-color .5s}
.qe-lead{z-index:2}
.qe-lead .qe-bt-tag{background-color:#132a1f;border-color:rgba(158,227,176,.55)}
.qe-lead .qe-bt-av{border-color:#9ee3b0}
.qe-lead .qe-bt-bd{background-color:#9ee3b0}
@keyframes qe-pz2{0%{transform:scale(.9);opacity:.9}100%{transform:scale(1.35);opacity:0}}
.qe-bt-ring{transform-box:fill-box;transform-origin:center;animation:qe-pz2 1.8s ease-out infinite}
@media (prefers-reduced-motion:reduce){.qe-bt-ring{animation:none}}
`;
